import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as api from '../utils/api'
import { compressAvatar } from '../utils/compressAvatar'
import { C, fontStack, btn, input, card } from '../constant/theme'

// Admin-only: create and manage regular (viewer) accounts.
export default function Users() {
  const navigate = useNavigate()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState({ name: '', email: '', password: '' })
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')

  const [editingId, setEditingId] = useState(null)
  const [editForm, setEditForm] = useState({ name: '', email: '', password: '' })
  const [editErr, setEditErr] = useState('')
  const [savingEdit, setSavingEdit] = useState(false)

  // Per-user avatar state: { [userId]: { busy, msg } }
  const [avatarState, setAvatarState] = useState({})
  const avatarRefs = useRef({})

  const load = async () => {
    setLoading(true)
    try {
      setUsers(await api.listUsers())
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const create = async (e) => {
    e.preventDefault()
    setError('')
    setCreating(true)
    try {
      const user = await api.createUser({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
      })
      setUsers((u) => [user, ...u])
      setForm({ name: '', email: '', password: '' })
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not create the account.')
    } finally {
      setCreating(false)
    }
  }

  const remove = async (u) => {
    if (!window.confirm(`Delete the account for ${u.email}? They will no longer be able to sign in.`)) return
    await api.deleteUser(u.id)
    setUsers((list) => list.filter((x) => x.id !== u.id))
  }

  const startEdit = (u) => {
    setEditingId(u.id)
    setEditForm({ name: u.name, email: u.email, password: '' })
    setEditErr('')
  }
  const cancelEdit = () => { setEditingId(null); setEditErr('') }
  const setEdit = (k) => (e) => setEditForm((f) => ({ ...f, [k]: e.target.value }))

  const saveEdit = async (u) => {
    setEditErr('')
    setSavingEdit(true)
    try {
      const updated = await api.updateUser(u.id, {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        password: editForm.password,
      })
      setUsers((list) => list.map((x) => (x.id === u.id ? updated : x)))
      setEditingId(null)
    } catch (err) {
      setEditErr(err?.response?.data?.message || 'Could not update the account.')
    } finally {
      setSavingEdit(false)
    }
  }

  const setAvState = (userId, patch) =>
    setAvatarState((s) => ({ ...s, [userId]: { ...s[userId], ...patch } }))

  const handleAvatarFile = async (userId, e) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    setAvState(userId, { busy: true, msg: null })
    try {
      const blob = await compressAvatar(file)
      const { signedUrl, gcsKey } = await api.signUserAvatarUpload(userId)
      await fetch(signedUrl, { method: 'PUT', body: blob, headers: { 'Content-Type': 'image/jpeg' } })
      const updated = await api.confirmUserAvatarUpload(userId, gcsKey)
      setUsers((list) => list.map((x) => (x.id === userId ? updated : x)))
      setAvState(userId, { busy: false, msg: { ok: true, text: 'Photo updated.' } })
    } catch (err) {
      setAvState(userId, { busy: false, msg: { ok: false, text: err?.response?.data?.message || 'Upload failed.' } })
    }
  }

  const handleRemoveAvatar = async (userId) => {
    setAvState(userId, { busy: true, msg: null })
    try {
      const updated = await api.removeUserAvatar(userId)
      setUsers((list) => list.map((x) => (x.id === userId ? updated : x)))
      setAvState(userId, { busy: false, msg: { ok: true, text: 'Photo removed.' } })
    } catch (err) {
      setAvState(userId, { busy: false, msg: { ok: false, text: err?.response?.data?.message || 'Could not remove photo.' } })
    }
  }

  return (
    <div style={S.root}>
      <header style={S.header}>
        <button style={btn('ghost')} onClick={() => navigate('/admin')}>← Trips</button>
        <strong style={{ color: C.text }}>User management</strong>
        <a href="/profile" style={S.link}>Profile</a>
      </header>

      <main style={S.main}>
        <div style={card}>
          <h3 style={S.h3}>Create a viewer account</h3>
          <p style={{ fontSize: 13, color: C.textSoft, marginTop: 0 }}>
            Viewers can sign in and browse all trips, but can't create or edit anything.
            Share the email + password with the person you're inviting.
          </p>
          <form onSubmit={create}>
            <div style={S.formRow}>
              <Field label="Name"><input style={input} value={form.name} onChange={set('name')} placeholder="Jane Doe" /></Field>
              <Field label="Email"><input style={input} type="email" value={form.email} onChange={set('email')} placeholder="jane@example.com" /></Field>
              <Field label="Password"><input style={input} value={form.password} onChange={set('password')} placeholder="At least 6 characters" /></Field>
            </div>
            {error && <div style={S.error}>⚠️ {error}</div>}
            <button style={{ ...btn('primary'), marginTop: 4 }} type="submit" disabled={creating}>
              {creating ? 'Creating…' : '+ Create account'}
            </button>
          </form>
        </div>

        <h2 style={S.title}>Accounts</h2>
        {loading ? (
          <p style={{ color: C.textSoft }}>Loading…</p>
        ) : (
          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            {users.map((u, i) => {
              const avSt = avatarState[u.id] || {}
              const initials = u.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()
              return (
                <div key={u.id} style={{ ...S.userRow, borderTop: i === 0 ? 'none' : `1px solid ${C.cardBorder}` }}>
                  {/* Avatar column */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                    <div
                      style={S.avatarWrap}
                      title="Click to change photo"
                      onClick={() => !avSt.busy && avatarRefs.current[u.id]?.click()}
                    >
                      {u.avatarUrl
                        ? <img src={u.avatarUrl} alt={u.name} style={S.avatarImg} />
                        : <div style={S.avatarInitials}>{initials}</div>
                      }
                      {avSt.busy && <div style={S.avatarSpinner}>…</div>}
                    </div>
                    <input
                      ref={(el) => { avatarRefs.current[u.id] = el }}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => handleAvatarFile(u.id, e)}
                    />
                    {u.avatarUrl && !avSt.busy && (
                      <button
                        style={{ ...btn('danger'), padding: '3px 8px', fontSize: 11 }}
                        onClick={() => handleRemoveAvatar(u.id)}
                        type="button"
                      >
                        Remove
                      </button>
                    )}
                    {avSt.msg && (
                      <div style={{ fontSize: 11, color: avSt.msg.ok ? C.green : C.danger, textAlign: 'center', maxWidth: 60 }}>
                        {avSt.msg.text}
                      </div>
                    )}
                  </div>

                  {/* Edit / info column */}
                  {editingId === u.id ? (
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={S.editGrid}>
                        <Field label="Name"><input style={input} value={editForm.name} onChange={setEdit('name')} /></Field>
                        <Field label="Email"><input style={input} type="email" value={editForm.email} onChange={setEdit('email')} /></Field>
                        <Field label="Reset password"><input style={input} value={editForm.password} onChange={setEdit('password')} placeholder="Leave blank to keep" /></Field>
                      </div>
                      {editErr && <div style={S.error}>⚠️ {editErr}</div>}
                      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                        <button style={btn('primary')} onClick={() => saveEdit(u)} disabled={savingEdit}>{savingEdit ? 'Saving…' : 'Save'}</button>
                        <button style={btn('ghost')} onClick={cancelEdit}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={S.userName}>
                          {u.name}
                          {u.isAdmin && <span style={S.adminBadge}>admin</span>}
                        </div>
                        <div style={S.userEmail}>{u.email}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button style={btn('ghost')} onClick={() => startEdit(u)}>Edit</button>
                        {u.isAdmin ? (
                          <span style={{ fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}>admin · .env</span>
                        ) : (
                          <button style={btn('danger')} onClick={() => remove(u)}>Delete</button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </main>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 180 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: '#2e3d52' }}>{label}</label>
      {children}
    </div>
  )
}

const S = {
  root: { minHeight: '100vh', background: C.bg, fontFamily: fontStack },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '14px 24px', background: '#fff', borderBottom: `1px solid ${C.cardBorder}` },
  link: { fontSize: 13, color: C.navy2, textDecoration: 'none', fontWeight: 600 },
  main: { maxWidth: 820, margin: '0 auto', padding: '24px' },
  h3: { margin: '0 0 8px', fontSize: 15, color: C.text },
  formRow: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 },
  editGrid: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 },
  title: { fontSize: 18, fontWeight: 800, color: C.text, margin: '24px 0 12px' },
  error: { background: C.dangerBg, color: C.danger, borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 },
  userRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 16px' },
  userName: { fontSize: 14.5, fontWeight: 700, color: C.text, display: 'flex', alignItems: 'center', gap: 8 },
  adminBadge: { fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#cdd8e8', color: C.navy2 },
  userEmail: { fontSize: 13, color: C.textSoft, marginTop: 2 },
  avatarWrap: {
    position: 'relative', width: 48, height: 48, borderRadius: '50%',
    overflow: 'hidden', cursor: 'pointer', flexShrink: 0,
    border: `2px solid ${C.cardBorder}`,
  },
  avatarImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  avatarInitials: {
    width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: `linear-gradient(135deg, ${C.navy2} 0%, #0d2640 100%)`,
    color: '#fff', fontSize: 16, fontWeight: 800,
  },
  avatarSpinner: {
    position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontSize: 14, fontWeight: 700,
  },
}
